import { type TreeLevel, type TreeNode } from '~/utils/tree/treeTypes';

/** One node on the way to a match: the match's ancestors outermost first, then the match itself. */
export type TreePathEntry<TValue> = {
  id: string;
  value: TValue;
};

export type BuildTreeFromAncestorsOptions<TItem, TValue> = {
  /**
   * What to keep when the same node is placed twice — it can arrive first as an ancestor, carrying
   * only what a hit's ancestry named it, and later as a match of its own carrying everything.
   */
  mergeValues: (existing: TValue, incoming: TValue) => TValue;
  /** The chain from the outermost ancestor down to the match; the last entry is the match. */
  toPath: (item: TItem) => readonly TreePathEntry<TValue>[];
};

const upsertNode = <TValue>(
  level: TreeLevel<TValue>,
  { id, value }: TreePathEntry<TValue>,
  isMatch: boolean,
  mergeValues: (existing: TValue, incoming: TValue) => TValue,
): TreeNode<TValue> => {
  const existing = level.get(id);

  if (existing) {
    existing.value = mergeValues(existing.value, value);
    // Sticky: placing a node again as a plain ancestor must not take its match flag back off.
    existing.isMatch = existing.isMatch || isMatch;

    return existing;
  }

  const node: TreeNode<TValue> = { children: new Map(), id, isExpandedByDefault: true, isMatch, value };

  level.set(id, node);

  return node;
};

/**
 * Search hits as a tree: every hit keeps its place under its ancestors, because a match only means
 * something next to where it lives. An ancestor nothing matched is synthesized on the way down and
 * flagged `isMatch: false` — it is there to say where the hit sits, not as one of the hits. Nodes
 * open by default, since a subtree left shut would hide the very hit it was built to place.
 *
 * Placing the same node twice tops it up rather than replacing it, so the order hits arrive in does
 * not decide what each node ends up knowing about itself.
 */
export const buildTreeFromAncestors = <TItem, TValue>(
  items: readonly TItem[],
  { mergeValues, toPath }: BuildTreeFromAncestorsOptions<TItem, TValue>,
): TreeLevel<TValue> => {
  const roots: TreeLevel<TValue> = new Map();

  for (const item of items) {
    const path = toPath(item);
    let level = roots;

    path.forEach((entry, index) => {
      level = upsertNode(level, entry, index === path.length - 1, mergeValues).children;
    });
  }

  return roots;
};
